import React, { useEffect, useState, useMemo } from 'react';
import BaseTile from '../../../components/Tile/BaseTile';
import { useGlobalAPI } from '../../../hooks/useGlobalAPI';

const Row = ({ label, value, isTotal = false }) => (
  <div className="flex items-center justify-between py-1 text-sm">
    <span className={`flex-1 min-w-0 truncate pr-3 ${isTotal ? 'font-bold text-blue-600' : ''}`} title={label}>{label}</span>
    <span className={`ml-2 shrink-0 whitespace-nowrap ${isTotal ? 'font-bold text-blue-600' : 'font-semibold text-gray-800'}`}>{value.toLocaleString()}</span>
  </div>
);

const ActivityCountsTile = ({ activeFile, maxItems = 20 }) => {
  const api = useGlobalAPI(activeFile);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use POST endpoint with global filters automatically applied
      const response = await api.post('/ocel/activity-counts', {});
      setData(response?.activityCounts || {});
    } catch (err) {
      setError(err?.message || 'Failed to load activity counts');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when filters or active file change
  useEffect(() => {
    if (activeFile) {
      fetchData();
    }
  }, [api.currentFilters, activeFile]);

  const sortedEntries = useMemo(() => {
    if (!data) return [];
    return Object.entries(data).sort((a, b) => b[1] - a[1]);
  }, [data]);

  const total = useMemo(() => sortedEntries.reduce((acc, [, v]) => acc + (v || 0), 0), [sortedEntries]);
  const topEntries = sortedEntries.slice(0, maxItems);

  return (
    <BaseTile
      title="Events per Activity"
      loading={loading}
      error={error}
      minHeight="lg"
      padding="default"
    >
      <div className="flex flex-col gap-2 h-full">
        {topEntries.length === 0 && !loading && !error && (
          <div className="text-center text-gray-500 text-sm">No activities found.</div>
        )}
        {total > 0 && (
          <div className="pb-2 border-b border-gray-200">
            <Row label="Total events:" value={total} isTotal={true} />
          </div>
        )}
        <div className="flex-1 overflow-auto">
          {topEntries.map(([label, count]) => (
            <Row key={label} label={label} value={count} />
          ))}
        </div>
      </div>
    </BaseTile>
  );
};

export default ActivityCountsTile;
